import type { Orientation, ScalingMode } from '../manifest/types.js';

/**
 * Scaling geometry (optisigns-parity §1: fit / fill / stretch plus a background fill
 * colour; design-addendum §5: a landscape rendition on a portrait screen is
 * pillarboxed and logged, never dropped).
 *
 * `fit` is the only default. It letterboxes or pillarboxes and never distorts.
 */

export interface Size {
  readonly width: number;
  readonly height: number;
}

export interface Layout {
  readonly width: number;
  readonly height: number;
  readonly offset_x: number;
  readonly offset_y: number;
  readonly scale_x: number;
  readonly scale_y: number;
  readonly letterboxed: boolean;
  readonly pillarboxed: boolean;
  readonly cropped: boolean;
  readonly distorted: boolean;
}

export const DEFAULT_SCALING_MODE: ScalingMode = 'fit';
export const DEFAULT_BACKGROUND_COLOR = '#000000';

export function computeLayout(source: Size, screen: Size, mode: ScalingMode): Layout {
  if (source.width <= 0 || source.height <= 0 || screen.width <= 0 || screen.height <= 0) {
    return {
      width: screen.width,
      height: screen.height,
      offset_x: 0,
      offset_y: 0,
      scale_x: 1,
      scale_y: 1,
      letterboxed: false,
      pillarboxed: false,
      cropped: false,
      distorted: false,
    };
  }

  if (mode === 'stretch') {
    const scaleX = screen.width / source.width;
    const scaleY = screen.height / source.height;
    return {
      width: screen.width,
      height: screen.height,
      offset_x: 0,
      offset_y: 0,
      scale_x: scaleX,
      scale_y: scaleY,
      letterboxed: false,
      pillarboxed: false,
      cropped: false,
      distorted: Math.abs(scaleX - scaleY) > 1e-9,
    };
  }

  const ratioX = screen.width / source.width;
  const ratioY = screen.height / source.height;
  const scale = mode === 'fill' ? Math.max(ratioX, ratioY) : Math.min(ratioX, ratioY);

  const width = source.width * scale;
  const height = source.height * scale;

  return {
    width,
    height,
    offset_x: (screen.width - width) / 2,
    offset_y: (screen.height - height) / 2,
    scale_x: scale,
    scale_y: scale,
    letterboxed: mode === 'fit' && height < screen.height - 0.5,
    pillarboxed: mode === 'fit' && width < screen.width - 0.5,
    cropped: mode === 'fill' && (width > screen.width + 0.5 || height > screen.height + 0.5),
    distorted: false,
  };
}

export function orientationOf(size: Size): Orientation {
  return size.height > size.width ? 'portrait' : 'landscape';
}

/** design-addendum §5: worth a warning, never a dropped item. */
export function isOrientationMismatch(source: Size, screen: Size): boolean {
  return orientationOf(source) !== orientationOf(screen);
}
